export type ScopeSubjectKind = 'company' | 'country' | 'system'
export type ScopeObjective = 'dependencies' | 'risk' | 'opportunity' | 'competition' | 'public-impact'
export type ScopeAudience = 'executive' | 'investor' | 'infrastructure' | 'public-sector' | 'community'
export type ScopeCadence = 'snapshot' | 'monitoring' | 'custom'

export interface ScopeRecommendationInput {
  subjectKind: ScopeSubjectKind
  subjectName: string
  objective: ScopeObjective
  audience: ScopeAudience
  cadence: ScopeCadence
}

export interface ScopeRecommendation {
  product: string
  price: string
  headline: string
  evidencePlan: string[]
  inquiry: string
}

const objectiveLabels: Record<ScopeObjective, string> = {
  dependencies: 'Physical and institutional dependencies',
  risk: 'Delivery risks and bottlenecks',
  opportunity: 'Capacity gaps and opportunities',
  competition: 'Competitive position and peer comparison',
  'public-impact': 'Policy, workforce, and community impact',
}

const audienceLabels: Record<ScopeAudience, string> = {
  executive: 'Executive or strategy team',
  investor: 'Investor or analyst',
  infrastructure: 'Infrastructure or technical team',
  'public-sector': 'Public agency or policymaker',
  community: 'Community or public-interest stakeholder',
}

const cadenceProducts: Record<ScopeCadence, { product: string; price: string; cadenceLabel: string }> = {
  snapshot: { product: 'Core evidence brief', price: '$750', cadenceLabel: 'Current evidence snapshot' },
  monitoring: { product: 'Three-brief monitoring series', price: '$1,800', cadenceLabel: 'Three updates over an agreed monitoring period' },
  custom: { product: 'Custom atlas or research engagement', price: 'Scoped quote', cadenceLabel: 'Custom dataset, geography, or recurring program' },
}

const objectiveEvidence: Record<ScopeObjective, string[]> = {
  dependencies: ['Verified subject baseline', 'Compute, facility, grid, power, capital, and policy dependency chain', 'Bottlenecks and unsupported assumptions', 'Open questions and monitoring triggers'],
  risk: ['Verified baseline and project status', 'Risk drivers across physical and institutional dependencies', 'Affected actors, geographies, and timelines', 'Trigger conditions and unresolved evidence'],
  opportunity: ['Current capacity and positioning', 'Documented gaps across the dependency chain', 'Relevant companies, agencies, projects, and capital', 'Catalysts, constraints, and evidence limits'],
  competition: ['Comparable peer set and declared strategy', 'Company-reported claims separated from delivery evidence', 'Infrastructure, capital, and policy position', 'Recent changes and unresolved differentiators'],
  'public-impact': ['Project and policy baseline', 'Grid, power, water, equipment, workforce, and community dependencies', 'Public proceedings and stakeholder positions', 'Documented benefits, burdens, unknowns, and monitoring triggers'],
}

export function recommendScope(input: ScopeRecommendationInput): ScopeRecommendation {
  const offer = cadenceProducts[input.cadence]
  const objective = objectiveLabels[input.objective]
  const audience = audienceLabels[input.audience]
  const subjectType = input.subjectKind === 'company' ? 'company' : input.subjectKind === 'country' ? 'country' : 'system issue'
  const headline = `${objective} for ${input.subjectName}`
  const inquiry = [
    'OMIKINA research scope request',
    '',
    `Subject: ${input.subjectName} (${subjectType})`,
    `Decision focus: ${objective}`,
    `Audience: ${audience}`,
    `Cadence: ${offer.cadenceLabel}`,
    `Recommended format: ${offer.product} — ${offer.price}`,
    '',
    'Intended use: [add intended use]',
    'Deadline: [add deadline]',
    'Additional boundary or exclusion: [optional]',
  ].join('\n')
  return { product: offer.product, price: offer.price, headline, evidencePlan: objectiveEvidence[input.objective], inquiry }
}

